import React, { useCallback, useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { CircularIndeterminate, FormButton } from "components";
import { getEstructura } from "../services/estructuraService";
import { TIPO, soloValidos, parentKey, targetKey, nivelPropio, tiposHabilitadosPorComponenteMaximo } from "../utils/estructura";
import { RNHDepartamentoGet } from "services/services-mia/programa-salud/estructuras/RNHDepartamentoGet";
import { RNHDivisionGet } from "services/services-mia/programa-salud/estructuras/RNHDivisionGet";
import { RNHUnidadGet } from "services/services-mia/programa-salud/estructuras/RNHUnidadGet";
import { RNHEstablecimientoGet } from "services/services-mia/programa-salud/estructuras/RNHEstablecimientoGet";
import { RNHEstablecimientoEstructuraIns } from "services/services-mia/programa-salud/estructuras/RNHEstablecimientoEstructuraIns";
import { RNHEstablecimientoEstructuraDel } from "services/services-mia/programa-salud/estructuras/RNHEstablecimientoEstructuraDel";
import styles from "./ModalAsignacion.module.scss";

const cx = (...cls) => cls.filter(Boolean).join(" ");

// En error respuestaDe deja el mensaje del back en error.mensaje → se usa ese.
const msgError = (r, fallback) => r.error?.mensaje || fallback;

// Descripción de cada entidad asignable: catálogo, accesores y ubicación en Ins/nodo.
//   insKey     → clave que espera RNHEstablecimientoEstructuraIns para ese tipo
//   idKeyNodo  → campo del nodo de estructura que referencia el id de catálogo
const ENTIDADES = {
  [TIPO.DEPARTAMENTO]: {
    label: "Departamentos",
    fetch: (c) => RNHDepartamentoGet(c),
    getId: (i) => i.rnhDepartamentoId,
    getNombre: (i) => i.rnhDepartamentoNombre,
    insKey: "rnhDepartamentoId",
    idKeyNodo: "rnhDepartamentoId"
  },
  [TIPO.DIVISION]: { label: "Divisiones", fetch: (c, target) => RNHDivisionGet(c, { disponibles: "Y", hospitalId: target?.hospitalId }), getId: (i) => i.rnhDivisionId, getNombre: (i) => i.rnhDivisionNombre, insKey: "rnhDivisionId", idKeyNodo: "rnhDivisionId" },
  // soloDisponibles: "Y" → solo unidades NO asignadas todavía (evita doble asignación:
  // una unidad directa al depto no puede estar además en una división del depto, y viceversa).
  // Es exclusivo del organigrama; en el resto de la app no se pasa.
  [TIPO.UNIDAD]: { label: "Unidades", fetch: (c, target) => RNHUnidadGet(c, { disponibles: "Y", hospitalId: target?.hospitalId }), getId: (i) => i.rnhUnidadId, getNombre: (i) => i.rnhUnidadNombre, insKey: "rnhUnidadId", idKeyNodo: "rnhUnidadId" },
  // disponibles: "Y" + hospitalId → el back devuelve solo caps/puestos/postas NO asignados
  // en ese hospital (unicidad por hospital). El `tipo` que viaja al Get es el mismo código
  // de componente que usa el nodo de estructura: ya no hay dos vocabularios.
  [TIPO.CAPS]: { label: "CAPS", fetch: (c, target) => RNHEstablecimientoGet(c, { tipo: TIPO.CAPS, disponibles: "Y", hospitalId: target?.hospitalId }), getId: (i) => i.rnhEstablecimientoId, getNombre: (i) => i.rnhEstablecimientoNombre, insKey: "rnhCapsId", idKeyNodo: "rnhEstablecimientoHijoId" },
  [TIPO.PUESTO]: { label: "Puestos", fetch: (c, target) => RNHEstablecimientoGet(c, { tipo: TIPO.PUESTO, disponibles: "Y", hospitalId: target?.hospitalId }), getId: (i) => i.rnhEstablecimientoId, getNombre: (i) => i.rnhEstablecimientoNombre, insKey: "rnhPuestoId", idKeyNodo: "rnhEstablecimientoHijoId" },
  [TIPO.POSTA]: { label: "Postas", fetch: (c, target) => RNHEstablecimientoGet(c, { tipo: TIPO.POSTA, disponibles: "Y", hospitalId: target?.hospitalId }), getId: (i) => i.rnhEstablecimientoId, getNombre: (i) => i.rnhEstablecimientoNombre, insKey: "rnhPostaId", idKeyNodo: "rnhEstablecimientoHijoId" },
  // URESA: HOJA, a la altura de Puesto/Posta, y con el mismo catálogo que los demás.
  [TIPO.URESA]: { label: "URESA", fetch: (c, target) => RNHEstablecimientoGet(c, { tipo: TIPO.URESA, disponibles: "Y", hospitalId: target?.hospitalId }), getId: (i) => i.rnhEstablecimientoId, getNombre: (i) => i.rnhEstablecimientoNombre, insKey: "rnhUresaId", idKeyNodo: "rnhEstablecimientoHijoId" },
};

// A cada entidad se le puede asignar CUALQUIER tipo de nivel INFERIOR (directo o
// indirecto). La cantidad de tipos baja a medida que se baja de nivel. Hojas: Puesto y Posta.
const TIPOS_POR_TARGET = {
  // Dirección/hospital: todo
  [TIPO.DIRECCION]: [TIPO.DEPARTAMENTO, TIPO.DIVISION, TIPO.UNIDAD, TIPO.CAPS, TIPO.PUESTO, TIPO.POSTA, TIPO.URESA],
  [TIPO.DEPARTAMENTO]: [TIPO.DIVISION, TIPO.UNIDAD, TIPO.CAPS, TIPO.PUESTO, TIPO.POSTA, TIPO.URESA],
  [TIPO.DIVISION]: [TIPO.UNIDAD, TIPO.CAPS, TIPO.PUESTO, TIPO.POSTA, TIPO.URESA],
  [TIPO.UNIDAD]: [TIPO.CAPS, TIPO.PUESTO, TIPO.POSTA, TIPO.URESA],
  // ⚠ falta habilitar CAP como target (ver Ins del "establecimiento padre")
  [TIPO.CAPS]: [TIPO.PUESTO, TIPO.POSTA, TIPO.URESA],
};

// Contexto base del Ins: hospital + SOLO el id del nivel PROPIO del target (su padre directo).
// En el modelo flexible "todo directo" NO se manda la cadena de ancestros: mandar, p. ej.,
// depto + división al asignar una unidad hace que el back matchee el par (depto, división)
// contra la asignación YA existente de esa división y devuelva "división ya asignada".
// El id del NUEVO hijo lo agrega el caller con `ent.insKey`.
const baseIns = (t) => {
  if (t?.tipo === TIPO.DEPARTAMENTO) return { rnhEstablecimientoId: t.hospitalId, rnhDepartamentoId: t.departamentoId };
  if (t?.tipo === TIPO.DIVISION) return { rnhEstablecimientoId: t.hospitalId, rnhDivisionId: t.divisionId };
  if (t?.tipo === TIPO.UNIDAD) return { rnhEstablecimientoId: t.hospitalId, rnhUnidadId: t.unidadId };
  if (t?.tipo === TIPO.CAPS) return { rnhEstablecimientoId: t.hospitalId, rnhCapsId: t.capsId };
  return { rnhEstablecimientoId: t?.hospitalId }; // Dirección
};

// Agrupa los nodos por código de tipo, con una clave por cada tipo asignable.
const agrupar = (res) => {
  const v = soloValidos(res);
  return Object.fromEntries(
    Object.keys(ENTIDADES).map((cod) => [cod, v.filter((n) => n.rnhTipoEstructuraCod === cod)])
  );
};

/**
 * Modal único de asignación. Filtro de tipo + 2 columnas (Asignables / Asignados),
 * de a uno. Trae SUS PROPIOS hijos del target (no depende del estado expandido de la
 * vista). Al asignar/desasignar refresca sus Asignados y avisa con `onCambio`.
 *
 * @param {{tipo:"H"|"D"|"I"|"U"|"C", hospitalId, departamentoId?, divisionId?}} target
 * @param {() => void} onCambio  se llama tras cada alta/baja (para refrescar la vista)
 */
export default function ModalAsignacion({ target, onCambio, onCerrar }) {
  const credentials = useRecoilValue(userCredentials);
  const setSnackBarInfo = useSetRecoilState(snackbarData);

  // Pestañas que aplican al target (por jerarquía). Se MUESTRAN todas; las que el
  // COMPONENTE MÁXIMO del hospital no permite se ven deshabilitadas (no seleccionables).
  const tipos = TIPOS_POR_TARGET[target?.tipo] ?? [];
  const habilitadosNivel = tiposHabilitadosPorComponenteMaximo(target?.componenteMaximo);
  const tiposHabil = tipos.filter((t) => habilitadosNivel.includes(t));
  const [tipoSel, setTipoSel] = useState(null);
  // La pestaña activa siempre cae en una habilitada (por defecto, la primera).
  const tipoActivo = tiposHabil.includes(tipoSel) ? tipoSel : tiposHabil[0] ?? null;
  const ent = tipoActivo ? ENTIDADES[tipoActivo] : null;

  const [catalogos, setCatalogos] = useState({}); // cache { tipo: item[] }
  const [cargandoCat, setCargandoCat] = useState(false);
  const [hijosPorTipo, setHijosPorTipo] = useState({});
  const [cargandoHijos, setCargandoHijos] = useState(false);
  const [asignandoId, setAsignandoId] = useState(null);
  const [quitandoId, setQuitandoId] = useState(null);

  // Hijos ACTUALES del target (Asignados). Se traen solos (independiente de la vista).
  const cargarHijos = useCallback(async () => {
    if (!target?.hospitalId) return;
    setCargandoHijos(true);
    try {
      // Traemos TODA la estructura y filtramos los HIJOS DIRECTOS del target por su
      // parentKey. Usamos listAll (no el drill del Get) porque el drill no devuelve las
      // asignaciones directas que saltean niveles (ej. un puesto directo a una división).
      const { data: todos } = await getEstructura(credentials, {
        rnhEstablecimientoEstructuraHospitalId: target.hospitalId,
        listAll: "Y",
      });
      const clave = targetKey(target);
      const nodos = soloValidos(todos);
      // Los ancestros que REALMENTE existen en este hospital. Sin esto, un nodo que
      // arrastra ids de dominio de niveles no asignados se le atribuye a un padre que
      // no esta en el arbol y desaparece de Asignados.
      const presentes = new Set(nodos.map(nivelPropio).filter(Boolean));
      const hijos = nodos.filter((n) => parentKey(n, presentes) === clave);
      setHijosPorTipo(agrupar(hijos));
    } finally {
      setCargandoHijos(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials, target?.tipo, target?.hospitalId, target?.departamentoId, target?.divisionId, target?.unidadId, target?.capsId]);

  useEffect(() => {
    cargarHijos();
  }, [cargarHijos]);

  // Catálogo del tipo activo (cacheado).
  useEffect(() => {
    if (!tipoActivo || catalogos[tipoActivo]) return;
    let vivo = true;
    (async () => {
      setCargandoCat(true);
      try {
        const { data } = await ENTIDADES[tipoActivo].fetch(credentials, target);
        const arr = data.filter((x) => ENTIDADES[tipoActivo].getId(x) != null);
        if (vivo) setCatalogos((c) => ({ ...c, [tipoActivo]: arr }));
      } finally {
        if (vivo) setCargandoCat(false);
      }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoActivo]);

  // Fuerza recargar el catálogo del tipo activo (tras asignar/desasignar): así el ítem
  // recién DESASIGNADO vuelve a aparecer como disponible en Asignables, y el asignado desaparece.
  const refrescarCatalogo = async () => {
    if (!tipoActivo) return;
    setCargandoCat(true);
    try {
      const { data } = await ENTIDADES[tipoActivo].fetch(credentials, target);
      const arr = data.filter((x) => ENTIDADES[tipoActivo].getId(x) != null);
      setCatalogos((c) => ({ ...c, [tipoActivo]: arr }));
    } finally {
      setCargandoCat(false);
    }
  };

  const asignados = ent
    ? (hijosPorTipo[tipoActivo] ?? []).map((n) => ({
      catalogoId: n[ent.idKeyNodo] ?? n.rnhCatalogoId,
      nodeId: n.rnhEstablecimientoEstructuraId,
      nombre: n.rnhEstructuraNombre,
      // La necesita el Del para el control de concurrencia.
      version: n.rnhEstablecimientoEstructuraVersion,
    }))
    : [];
  const asignadosIds = asignados.map((a) => a.catalogoId);
  const catalogo = ent ? catalogos[tipoActivo] ?? [] : [];

  // Compatibilidad por jerarquía (containment). El depto/división cargados en la entidad son
  // RESTRICCIONES; `null`/'' = "sin restricción" (comodín → cuelga de cualquiera). Si tienen
  // valor, debe COINCIDIR con la cadena del target. Solo aplica a Divisiones/Unidades bajo un
  // Depto/División; CAPS/Puestos/Postas y todo lo que cuelga de la Dirección no se filtran
  // (de la Dirección cuelga cualquier cosa).
  const nivelCompatible = (valorItem, valorTarget) =>
    valorItem == null || valorItem === "" || String(valorItem) === String(valorTarget);

  const compatiblePorJerarquia = (item) => {
    if (tipoActivo === TIPO.UNIDAD) {
      // Unidad bajo una División V (depto padre D_V): division ∈ {∅, V} Y depto ∈ {∅, D_V}.
      if (target?.tipo === TIPO.DIVISION) {
        return nivelCompatible(item.rnhDivisionId, target.divisionId)
          && nivelCompatible(item.rnhDepartamentoId, target.departamentoId);
      }
      // Unidad directo al Depto D: depto ∈ {∅, D}. Aunque tenga una división propia, igual
      // se permite colgarla directo del depto (la división es un hogar sugerido, no una
      // cadena inmutable; la unicidad garantiza que viva en un solo lugar). NO aparece bajo
      // divisiones hermanas: la división sigue siendo restricción dura de "qué división".
      if (target?.tipo === TIPO.DEPARTAMENTO) {
        return nivelCompatible(item.rnhDepartamentoId, target.departamentoId);
      }
    }
    // División (catálogo) bajo un Depto D: depto ∈ {∅, D} (una división sin depto va a cualquiera).
    if (tipoActivo === TIPO.DIVISION && target?.tipo === TIPO.DEPARTAMENTO) {
      return nivelCompatible(item.rnhDepartamentoId, target.departamentoId);
    }
    return true; // sin filtro por jerarquía
  };

  const disponibles = catalogo.filter((item) => {
    if (asignadosIds.includes(ent.getId(item))) return false;
    return compatiblePorJerarquia(item);
  });

  const asignarUno = async (item) => {
    const id = ent.getId(item);
    setAsignandoId(id);
    try {
      const r = await RNHEstablecimientoEstructuraIns(credentials, { ...baseIns(target), [ent.insKey]: id });
      const ok = r.ok;
      setSnackBarInfo({ message: ok ? "Asignado correctamente" : msgError(r, "No se pudo asignar"), severity: ok ? "success" : "error", open: true });
      if (ok) {
        await Promise.all([cargarHijos(), refrescarCatalogo()]);
        onCambio?.();
      }
    } finally {
      setAsignandoId(null);
    }
  };

  const desasignarUno = async (asig) => {
    setQuitandoId(asig.nodeId);
    try {
      const r = await RNHEstablecimientoEstructuraDel(credentials, {
        id: asig.nodeId,
        version: asig.version,
      });
      const ok = r.ok;
      setSnackBarInfo({ message: ok ? "Desasignado correctamente" : msgError(r, "No se pudo desasignar"), severity: ok ? "success" : "error", open: true });
      if (ok) {
        await Promise.all([cargarHijos(), refrescarCatalogo()]);
        onCambio?.();
      }
    } finally {
      setQuitandoId(null);
    }
  };

  return (
    <div className={styles.contenedor}>
      {tipos.length > 1 && (
        <div className={styles.filtro}>
          {tipos.map((t) => {
            const habilitado = habilitadosNivel.includes(t);
            return (
              <button
                key={t}
                type="button"
                className={cx(
                  styles.filtroBtn,
                  t === tipoActivo && styles.filtroActivo,
                  !habilitado && styles.filtroDeshabilitado
                )}
                onClick={() => habilitado && setTipoSel(t)}
                disabled={!habilitado}
                title={habilitado ? undefined : "No disponible para el nivel de complejidad del hospital"}
              >
                {ENTIDADES[t].label}
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.columnas}>
        <section className={styles.columna}>
          <header className={styles.columnaHeaderCenter}>{ent?.label} asignables</header>
          <div className={styles.columnaBody}>
            {cargandoCat ? (
              <div className={styles.cargando}><CircularIndeterminate /></div>
            ) : disponibles.length === 0 ? (
              <span className={styles.vacio}>No hay {ent?.label.toLowerCase()} disponibles.</span>
            ) : (
              disponibles.map((item) => {
                const id = ent.getId(item);
                return (
                  <button
                    key={id}
                    type="button"
                    className={styles.itemAsignable}
                    onClick={() => asignarUno(item)}
                    disabled={asignandoId != null}
                  >
                    <span>{ent.getNombre(item)}</span>
                    <ChevronRightRoundedIcon fontSize="small" />
                  </button>
                );
              })
            )}
          </div>
        </section>

        <section className={styles.columna}>
          <header className={styles.columnaHeaderCenter}>{ent?.label === 'Departamentos' ? `${ent?.label} asignados` : `${ent?.label} asignadas`}</header>
          <div className={styles.columnaBody}>
            {cargandoHijos ? (
              <div className={styles.cargando}><CircularIndeterminate /></div>
            ) : asignados.length === 0 ? (
              <span className={styles.vacio}>Todavía no hay asignados.</span>
            ) : (
              asignados.map((a) => (
                <div key={a.nodeId} className={styles.itemAsignado}>
                  <span>{a.nombre}</span>
                  <button
                    type="button"
                    className={styles.quitar}
                    onClick={() => desasignarUno(a)}
                    disabled={quitandoId === a.nodeId}
                    title="Desasignar"
                  >
                    <ChevronLeftRoundedIcon fontSize="small" />
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* Botón "Cerrar" al pie, con el estilo estándar de los modales de la app. */}
      <FormButton oneButton closeButtonText="Cerrar" onCloseModal={onCerrar} />
    </div>
  );
}
